import {
  Autocomplete,
  Box,
  Chip,
  Container,
  Divider,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useState } from "react";
import GlobalHeader from "../../../components/GlobalHeader";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import { useUser } from "../../../hooks/useUser";
import styled from "@emotion/styled";
import useApi from "../../../hooks/useApi";
//icons
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate } from "react-router-dom";
import countries from "../../../utils/countries";
import defaultBanner from "../../../assets/portadaProductor2.jpg";
import {
  FaSpotify,
  FaFacebook,
  FaYoutube,
  FaInstagram,
  FaTiktok,
  FaMailBulk,
} from "react-icons/fa";
import useGeneros from "../../../hooks/useGeneros";
import {
  opcionesTipoArtista,
  gestionaMultiplesArtistas,
  maxGeneros,
  normalizeGeneros,
} from "../../../utils/tiposArtista";
import CampoPresskit from "../../../components/CampoPresskit";
import EliminarCuenta from "../../../components/EliminarCuenta";
import WhatsappConfig from "../../../components/WhatsappConfig";
import TelegramConfig from "../../../components/TelegramConfig";
import SubirImagenPerfil from "../../../components/SubirImagenPerfil";

const initForm = {
  nombre_media: "",
  pais: "",
  descripcion: "",
  pagina_web: "",
  facebook: "",
  instagram: "",
  youtube: "",
  spotify: "",
  tiktok: "",
};

const initFormPassword = {
  password: "",
  confirmPassword: "",
};

const countryOptions = Object.entries(countries).map(([key, value]) => ({
  value: key,
  label: value,
}));

const StyledButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 8,
  textTransform: "none",
}));

const EditProfileArtist = () => {
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { user, updateUser } = useUser();
  const { loadApi, loadingApi } = useApi();
  const navigate = useNavigate();
  const generos = useGeneros();
  const generoOptions = generos.map((item) => ({
    value: item,
    label: item,
  }));
  //States
  const [form, setForm] = useState(initForm);
  const [formPassword, setFormPassword] = useState(initFormPassword);
  const [tags, setTags] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [musicalGenders, setMusicalGenders] = useState(generoOptions);

  const [selectedFiles, setPreviewImage] = useState([]);
  const [bannerImgState, setBannerImgState] = useState(null);
  const [presskit, setPresskit] = useState(null);
  const [quitarPresskit, setQuitarPresskit] = useState(false);

  //UseEffect
  useEffect(() => {
    // Los géneros llegan como array, string JSON o string plano según cómo
    // se hayan guardado; siempre se trabaja con un array.
    setTags(normalizeGeneros(user?.profile?.generos_musicales));
    setPreviewImage(user?.profile?.bannerimg || defaultBanner);
  }, [user?.profile?.generos_musicales, user?.profile?.bannerimg]);

  // La lista de géneros es administrable y llega por API después del primer
  // render; sin esto el catálogo quedaría congelado en la lista estática de
  // respaldo y los géneros agregados desde el panel nunca aparecerían.
  useEffect(() => {
    setMusicalGenders(generos.map((item) => ({ value: item, label: item })));
  }, [generos]);

  //cambiar datos del state
  const handleInputChange = ({ target }, setState) => {
    const { name, value } = target;
    setState((adata) => ({
      ...adata,
      [name]: value,
    }));
  };

  //Cambiar datos del autocomplete
  const handleAutocompleteChange = (e, name) => {
    if (name) setForm((adata) => ({ ...adata, pais: name.value }));
    else setForm((adata) => ({ ...adata, pais: "mx" }));
  };

  // Cuántos géneros admite este perfil: los que gestionan varios artistas
  // (sello, manager, label) cargan un catálogo; el resto, uno solo. El tipo
  // puede haberse cambiado en este mismo formulario sin guardar todavía.
  const limiteGeneros = maxGeneros(form.tipo || user?.profile?.tipo);

  // Un sello, manager o label no carga un presskit propio: el presskit es de
  // cada artista y se sube en su ficha del roster.
  const multiartista = gestionaMultiplesArtistas(form.tipo || user?.profile?.tipo);

  const onDeleteTag = (value) => {
    setTags((prevTags) =>
      Array.isArray(prevTags) ? prevTags.filter((tag) => tag !== value) : []
    );
  };

  const handleAddTag = (value) => {
    const currentTags = Array.isArray(tags) ? tags : [];
    if (currentTags.includes(value)) {
      showSnack("¡Ya ingresaste este género!", "warning");
      return;
    }
    // Con un solo cupo, elegir otro género reemplaza al anterior en vez de
    // obligar a borrarlo primero.
    if (limiteGeneros === 1) {
      setTags([value]);
      return;
    }
    if (currentTags.length >= limiteGeneros) {
      showSnack(`¡Máximo ${limiteGeneros} géneros!`, "warning");
      return;
    }
    setTags((prev) => [...(Array.isArray(prev) ? prev : []), value]);
  };

  const handleSearchTags = (value) => {
    clearTimeout(typingTimeout);
    if (value.trim()) {
      const newTypingTimeout = setTimeout(() => {
        setMusicalGenders(
          generoOptions.filter((option) =>
            option.value.toLowerCase().includes(value.toLowerCase())
          )
        );
      }, 500);

      setTypingTimeout(newTypingTimeout);
    } else {
      setMusicalGenders(generoOptions);
    }
  };

  //Cambiar información del usuario
  const handleSubmitUser = async (e, oldBody) => {
    e.preventDefault();
    try {
      let body;
      if (Object.keys(oldBody).includes("password")) {
        if (oldBody.password === oldBody.confirmPassword)
          body = { password: oldBody.password };
        else {
          return showSnack("las contraseñas no coinciden", "error");
        }
      } else {
        body = oldBody;
      }
      const { data } = await loadApi("user", true, "put", body);
      showSnack(data.message);
      updateUser();
    } catch (error) {
      showSnack(error.message, "error");
    }
  };

  const handleChangeImg = async ({ target }) => {
    const [file] = target.files;
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setBannerImgState(file);
    }
  };

  const submitImage = async ({ target }) => {
    const file = bannerImgState;
    if(file == null) return;
    if (file) {
      try {
        const { data } = await loadApi(
          "artista/profile",
          true,
          "put",
          { bannerimg: file },
          true
        );

        showSnack(data.message);
        updateUser();
        setBannerImgState(null);
      } catch (error) {
        showSnack(error.message, "error");
      }
    } else {
      setPreviewImage(null);
    }
  };

  //Cambiar información publica
  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    const body = form;
    if (body.paypal_email) body.paypal_email = body.paypal_email.toLowerCase();
    if (Array.isArray(tags) && tags.length > 0)
      body["generos_musicales"] = tags;
    if (presskit) body.presskit = presskit;
    // No mandar el archivo significa "no lo toques"; borrarlo es explícito.
    if (quitarPresskit) body.quitar_presskit = "true";
    try {
      const { data } = await loadApi(
        "artista/profile",
        true,
        "put",
        body,
        true
      );
      showSnack(data.message);
      setPresskit(null);
      setQuitarPresskit(false);
      updateUser();
    } catch (error) {
      showSnack(error.message, "error");
    }
  };

  return (
    <Stack bgcolor="#f0f1f7" minHeight="calc(100vh - 40px)" pb={5}>
      <GlobalHeader />
      <Container maxWidth="md">
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Typography variant="h6" color="#3b3b3b">
            Editar perfil
          </Typography>
        </Stack>
        {/* Edit cart */}
        <Stack
          bgcolor="#ffffff"
          sx={{
            borderRadius: "20px",
            boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
          }}
          py={4}
          px={{ xs: 1, sm: 3 }}
        >
          {/* User info (solo lectura: nombre de usuario y email son inmutables) */}
          <Typography variant="subtitle1" color="#3b3b3b">
            Información de usuario
          </Typography>
          <Divider />
          <Stack gap={2} py={2}>
            <TextField
              size="small"
              name="name"
              type="text"
              label="Nombre cuenta"
              disabled
              helperText="El nombre de usuario no se puede modificar"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">@</InputAdornment>
                ),
              }}
              defaultValue={user?.name}
              sx={{
                maxWidth: "400px",
              }}
            />
            <TextField
              size="small"
              name="username"
              type="email"
              label="Correo electronico"
              disabled
              defaultValue={user?.username}
              sx={{
                maxWidth: "400px",
              }}
            />
          </Stack>
          {/* Profile info */}
          <Typography variant="subtitle1" color="#3b3b3b">
            Información publica
          </Typography>
          <Divider />
          <Stack
            component="form"
            role="form"
            gap={2}
            py={2}
            onSubmit={handleSubmitProfile}
          >
            <TextField
              size="small"
              name="nombre_media"
              type="text"
              label="Nombre"
              onChange={(e) => handleInputChange(e, setForm)}
              defaultValue={user?.profile?.nombre_media}
              sx={{
                maxWidth: "400px",
              }}
            />
            <Stack direction="row" gap={1} width="100%" maxWidth="400px">
              <Stack
                justifyContent="center"
                p={1}
                border=".5px solid #00000040"
                borderRadius={1}
              >
                <Box
                  component="img"
                  src={`https://flagcdn.com/${
                    form.pais || user?.profile?.pais
                  }.svg`}
                  alt="País"
                  width="30px"
                  height="20px"
                />
              </Stack>
              <Autocomplete
                disablePortal
                disableClearable
                defaultValue={countries[user?.profile?.pais]}
                onChange={handleAutocompleteChange}
                options={countryOptions}
                name="pais"
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" />
                )}
              />
            </Stack>
            <TextField
              size="small"
              name="descripcion"
              type="text"
              label="Descripcion"
              multiline={true}
              rows={7}
              onChange={(e) => handleInputChange(e, setForm)}
              defaultValue={user?.profile?.descripcion}
              sx={{
                maxWidth: "400px",
              }}
            />
            <Stack maxWidth="400px">
              <FormLabel sx={{ color: "grayText" }}>
                Tipo de Ecosistema musical
              </FormLabel>
              <Select
                defaultValue={user?.profile?.tipo}
                onChange={(e) => handleInputChange(e, setForm)}
                name="tipo"
                sx={{ fontWeight: "400" }}
                size="small"
              >
                {opcionesTipoArtista(user?.profile?.tipo).map((item, i) => (
                  <MenuItem key={i} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            {/* Presskit: solo para los individuales; ver arriba. */}
            {!multiartista && (
            <Stack maxWidth="400px">
              <CampoPresskit
                valor={user?.profile?.presskit}
                archivo={presskit}
                quitar={quitarPresskit}
                onElegir={(file) => {
                  setPresskit(file);
                  if (file) setQuitarPresskit(false);
                }}
                onQuitar={() => {
                  setQuitarPresskit(true);
                  setPresskit(null);
                }}
                onDeshacer={() => setQuitarPresskit(false)}
                showSnack={showSnack}
              />
            </Stack>
            )}
            {/* Géneros musicales */}
            <Stack maxWidth="400px" gap={1}>
              <FormLabel sx={{ color: "grayText", fontWeight: 600 }}>
                {limiteGeneros === 1
                  ? "Género musical:"
                  : `Géneros musicales (hasta ${limiteGeneros}):`}
              </FormLabel>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {(Array.isArray(tags) ? tags : []).map((item, i) => (
                  <Chip
                    label={item}
                    key={i}
                    onDelete={() => onDeleteTag(item)}
                    size="small"
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                placeholder="Busca tu genero aquí"
                onKeyUp={(e) => handleSearchTags(e.target.value)}
              />
              <Stack
                width="100%"
                maxHeight="400px"
                p={1}
                bgcolor="#f0f1f7"
                sx={{
                  boxSizing: "border-box",
                  overflowY: "auto",
                }}
                borderRadius={2}
                flexWrap="wrap"
                direction="row"
                gap={1}
                className="tagsList"
              >
                {musicalGenders.map((item, i) => (
                  <Chip
                    label={item.value}
                    key={i}
                    onClick={() => handleAddTag(item.value)}
                  />
                ))}
              </Stack>
            </Stack>
            {/* Social network */}
            <Stack maxWidth="400px" gap={3}>
              <FormLabel sx={{ color: "grayText", fontWeight: 600 }}>
                Redes Sociales y contacto:
              </FormLabel>
              <Stack gap={2}>
                <TextField
                  size="small"
                  name="email_contacto"
                  type="text"
                  label="Email de contacto"
                  onChange={(e) => handleInputChange(e, setForm)}
                  defaultValue={user?.profile?.email_contacto}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaMailBulk style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  size="small"
                  name="facebook"
                  type="text"
                  label="Cuenta de facebook"
                  onChange={(e) => handleInputChange(e, setForm)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaFacebook style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                  defaultValue={user?.profile?.facebook}
                />
                <TextField
                  size="small"
                  name="instagram"
                  type="text"
                  label="Cuenta de instagram"
                  onChange={(e) => handleInputChange(e, setForm)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaInstagram style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                  defaultValue={user?.profile?.instagram}
                />
                <TextField
                  size="small"
                  name="youtube"
                  type="text"
                  label="Cuenta de youtube"
                  onChange={(e) => handleInputChange(e, setForm)}
                  defaultValue={user?.profile?.youtube}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaYoutube style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  size="small"
                  name="spotify"
                  type="text"
                  label="Cuenta de spotify"
                  onChange={(e) => handleInputChange(e, setForm)}
                  defaultValue={user?.profile?.spotify}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSpotify style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  size="small"
                  name="tiktok"
                  type="text"
                  label="Cuenta de tiktok"
                  onChange={(e) => handleInputChange(e, setForm)}
                  defaultValue={user?.profile?.tiktok}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaTiktok style={{ color: "#F27224" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
            {/* Links and payments */}
            <Stack maxWidth="400px">
              <TextField
                size="small"
                name="pagina_web"
                type="text"
                label="Pagina web"
                onChange={(e) => handleInputChange(e, setForm)}
                defaultValue={user?.profile?.pagina_web}
              />
            </Stack>
            <Stack alignItems="end">
              <StyledButton
                loading={loadingApi.includes("put__artista/profile")}
                loadingPosition="start"
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                type="submit"
              >
                Guardar
              </StyledButton>
            </Stack>
          </Stack>
          {/* Change password */}
          <Typography variant="subtitle1" color="#3b3b3b">
            Cambiar contraseña
          </Typography>
          <Divider />
          <Stack
            component="form"
            role="form"
            gap={2}
            py={2}
            onSubmit={(e) => handleSubmitUser(e, formPassword)}
          >
            <TextField
              size="small"
              name="password"
              type="password"
              label="Nueva contraseña"
              onChange={(e) => handleInputChange(e, setFormPassword)}
              sx={{
                maxWidth: "400px",
              }}
            />
            <TextField
              size="small"
              name="confirmPassword"
              type="password"
              label="Confirmar nueva contraseña"
              onChange={(e) => handleInputChange(e, setFormPassword)}
              sx={{
                maxWidth: "400px",
              }}
            />
            <Stack alignItems="end">
              <StyledButton
                loading={loadingApi.includes("put__user")}
                loadingPosition="start"
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                type="submit"
              >
                Guardar
              </StyledButton>
            </Stack>
          </Stack>

          {/* Imágenes: foto de perfil y banner */}
          <Typography variant="subtitle1" color="#3b3b3b">
            Imágenes
          </Typography>
          <Divider />
          <Stack gap={4} py={3}>
            <SubirImagenPerfil
              tipo="profileimg"
              endpoint="artista/profile"
              currentImage={user?.profile?.profileimg}
              showSnack={showSnack}
            />
            <SubirImagenPerfil
              tipo="bannerimg"
              endpoint="artista/profile"
              currentImage={user?.profile?.bannerimg}
              showSnack={showSnack}
            />
          </Stack>


          <WhatsappConfig showSnack={showSnack} />
          <TelegramConfig showSnack={showSnack} />
          <EliminarCuenta showSnack={showSnack} />
        </Stack>
      </Container>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </Stack>
  );
};

export default EditProfileArtist;
